import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import { useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Asterisk } from '@/components/generals/asterisk'
import { SectionLabel } from '@/components/generals/section-label'

interface TestimonialsProps {
    testimonials: Testimonial[]
}

const pad = (value: number): string => String(value).padStart(2, '0')

export const Testimonials: FC<TestimonialsProps> = ({ testimonials }): ReactNode => {
    const [active, setActive] = useState(0)
    const count = testimonials.length

    if (count === 0) return null

    const current = Math.min(active, count - 1)

    const go = (direction: number): void => {
        setActive(index => (index + direction + count) % count)
    }

    return (
        <section id='testimonials' className='border-t border-foreground/10 py-20 md:py-32'>
            <div className='page-container'>
                <div className='flex flex-wrap items-end justify-between gap-8'>
                    <div>
                        <SectionLabel className='text-muted-foreground'>
                            Kind words
                        </SectionLabel>

                        <h2 className='mt-5 font-heading text-[clamp(2.75rem,6vw,5.75rem)] font-semibold leading-[0.92] tracking-[-0.045em]'>
                            Clients, <em className='font-serif font-normal italic tracking-[-0.02em] text-brand'>unfiltered</em>
                        </h2>
                    </div>

                    {count > 1 && (
                        <div className='flex items-center gap-3'>
                            <span aria-hidden='true' className='mr-2 font-mono text-sm text-muted-foreground'>
                                {pad(current + 1)} / {pad(count)}
                            </span>

                            <button
                                type='button'
                                onClick={() => go(-1)}
                                aria-label="Previous testimonial"
                                className='grid size-12 place-items-center rounded-full border border-foreground/20 transition-colors hover:border-foreground hover:bg-foreground hover:text-background'
                            >
                                <ArrowLeft className='size-5' />
                            </button>

                            <button
                                type='button'
                                onClick={() => go(1)}
                                aria-label="Next testimonial"
                                className='grid size-12 place-items-center rounded-full border border-foreground/20 transition-colors hover:border-foreground hover:bg-foreground hover:text-background'
                            >
                                <ArrowRight className='size-5' />
                            </button>
                        </div>
                    )}
                </div>

                <div aria-live='polite' className='mt-12 grid md:mt-16'>
                    {testimonials.map((testimonial, index) => {
                        const isActive = index === current

                        return (
                            <figure
                                key={testimonial.id}
                                aria-hidden={!isActive}
                                inert={!isActive}
                                className={cn(
                                    'col-start-1 row-start-1 transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]',
                                    isActive ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0'
                                )}
                            >
                                <blockquote className='max-w-6xl text-balance font-heading text-[clamp(1.75rem,4vw,3.75rem)] font-medium leading-[1.08] tracking-[-0.03em]'>
                                    <span aria-hidden='true' className='text-brand'>“</span>
                                    <span data-mdlfy-cms={`testimonials:${testimonial.id}:quote`}>{testimonial.quote}</span>
                                    <span aria-hidden='true' className='text-brand'>”</span>
                                </blockquote>

                                <figcaption className='mt-10 flex items-center gap-4'>
                                    <span aria-hidden='true' className='grid size-12 shrink-0 place-items-center rounded-full bg-brand text-brand-foreground'>
                                        <Asterisk className='size-5' />
                                    </span>

                                    <span className='min-w-0'>
                                        <span data-mdlfy-cms={`testimonials:${testimonial.id}:author`} className='block font-semibold'>
                                            {testimonial.author}
                                        </span>

                                        <span className='block text-sm text-muted-foreground'>
                                            {testimonial.role && (
                                                <span data-mdlfy-cms={`testimonials:${testimonial.id}:role`}>{testimonial.role}</span>
                                            )}

                                            {testimonial.role && testimonial.company && ', '}

                                            {testimonial.company && (
                                                <span data-mdlfy-cms={`testimonials:${testimonial.id}:company`}>{testimonial.company}</span>
                                            )}
                                        </span>
                                    </span>
                                </figcaption>
                            </figure>
                        )
                    })}
                </div>

                {count > 1 && (
                    <div className='mt-12 flex flex-wrap gap-2'>
                        {testimonials.map((testimonial, index) => (
                            <button
                                key={testimonial.id}
                                type='button'
                                onClick={() => setActive(index)}
                                aria-pressed={index === current}
                                className={cn(
                                    'rounded-full border px-4 py-2 text-sm transition-colors',
                                    index === current
                                        ? 'border-foreground bg-foreground text-background'
                                        : 'border-foreground/20 text-foreground/75 hover:border-foreground hover:text-foreground'
                                )}
                            >
                                {testimonial.company ? (
                                    <span data-mdlfy-cms={`testimonials:${testimonial.id}:company`}>{testimonial.company}</span>
                                ) : (
                                    <span data-mdlfy-cms={`testimonials:${testimonial.id}:author`}>{testimonial.author}</span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}
